import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  DataTableComponent,
  DataTableColumn,
} from "../../components/EmailDataTableComponent/DataTableComponent";

interface RatePriceFluctuationComponentProps {
  basePrice: number;
  delta: number;
  dataPoints?: number; // max number of points (default: 8)
}

interface DataPoint {
  date: string;
  price: number;
  vendorName: string;
  email: string;
  source?: string;
}

const fakeVendors: DataPoint[] = [
  {
    date: "",
    price: 0,
    vendorName: "FreightOne",
    email: "contact@freightone.com",
  },
  {
    date: "",
    price: 0,
    vendorName: "Cargo Solutions",
    email: "info@cargosolutions.com",
  },
  {
    date: "",
    price: 0,
    vendorName: "Express Freight",
    email: "support@expressfreight.com",
  },
  {
    date: "",
    price: 0,
    vendorName: "Global Haul",
    email: "sales@globalhaul.com",
  },
  {
    date: "",
    price: 0,
    vendorName: "QuickCargo",
    email: "hello@quickcargo.com",
  },
  {
    date: "",
    price: 0,
    vendorName: "ShipRight",
    email: "contact@shipright.com",
  },
  { date: "", price: 0, vendorName: "LogiTrans", email: "info@logitrans.com" },
  {
    date: "",
    price: 0,
    vendorName: "Prime Movers",
    email: "service@primemovers.com",
  },
];

export const RatePriceFluctuationComponent: React.FC<
  RatePriceFluctuationComponentProps
> = ({ basePrice, delta, dataPoints = 8 }) => {
  // Define start and end dates.
  const startDate = new Date("2024-03-25");
  const endDate = new Date("2025-03-25");
  const timeDiff = endDate.getTime() - startDate.getTime();
  const interval = timeDiff / (dataPoints - 1);

  // Generate chart and table data.
  const data: DataPoint[] = useMemo(() => {
    return Array.from({ length: dataPoints }, (_, i) => {
      const currentDate = new Date(startDate.getTime() + i * interval);
      // Random fluctuation between -delta and +delta.
      const fluctuation = Math.random() * (2 * delta) - delta;
      const price = Math.round(basePrice + fluctuation);
      const formattedDate = currentDate.toISOString().split("T")[0];
      const vendor = fakeVendors[i % fakeVendors.length];
      const imageSources = [
        "/www/assets/123loadboard_logo.jpg",
        "/www/assets/dat_logo.png",
        "/www/assets/truckstop_logo.jpg",
      ];
      const source =
        Math.random() < 0.5
          ? imageSources[Math.floor(Math.random() * imageSources.length)]
          : "your deal";
      return {
        date: formattedDate,
        price,
        vendorName: vendor.vendorName,
        email: vendor.email,
        source,
      };
    });
  }, [basePrice, delta, dataPoints, interval]);

  const columns: DataTableColumn<DataPoint>[] = [
    { columnName: "Date", fieldName: "date" },
    { columnName: "Price", fieldName: "price" },
    { columnName: "Vendor Name", fieldName: "vendorName" },
    { columnName: "Email", fieldName: "email" },
    {
      columnName: "Source",
      renderFn: (row) =>
        row.source &&
        (row.source.endsWith(".jpg") || row.source.endsWith(".png")) ? (
          <img src={row.source} alt="Source" style={{ maxHeight: 24 }} />
        ) : (
          row.source
        ),
    },
  ];
  console.log(data);

  return (
    <Box>
      <Typography variant="h6" gutterBottom width="100%" textAlign="center">
        Price Trend for This Lane
      </Typography>
      <Box width="100%" height={300}>
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis
              domain={[basePrice - delta - 10, basePrice + delta + 10]}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `$${Math.round(value)}`}
            />
            <Tooltip />
            <Line
              type="linear"
              dataKey="price"
              stroke="#8884d8"
              strokeWidth={2}
              dot
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <Box mt={4}>
        <Typography variant="h6" gutterBottom></Typography>
        <Box sx={{ height: "300px" }}>
          <DataTableComponent
            stickyHeader
            rows={data}
            columns={columns}
            keyField="date"
            sx={{ height: "300px" }}
          />
        </Box>
      </Box>
    </Box>
  );
};
